// Marketing Pages — list view (M4, motiflink §2.4). Migrated to the console
// component system in M7c / P6.
//
// Marketing is SSR since M4, so there is no draft/publish: a page is either
// active (live) or not, and snapshots are the undo. The list reflects that —
// the old Status column is a live/hidden pill.

import { Button, Input } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import {
  ConfirmDialog,
  DataTable,
  FilterBar,
  Icon,
  PageHeader,
  Pagination,
  StatusBadge,
  actionsColumn,
  paginate,
  sortRows,
  useTableState,
  useToast,
  type Column,
} from '~/components';
import { ApiError } from '~/lib/api';
import { createPage, deletePage, listPages, updatePage, type PageSummary } from '~/lib/pages';

export const Route = createFileRoute('/_authed/pages/')({
  component: PagesListPage,
});

function PagesListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PageSummary | null>(null);
  const { toast } = useToast();

  const table = useTableState({
    // Homepage first, then alphabetical — the order an editor scans in.
    defaultSort: { key: 'slug', direction: 'asc' },
    filterKeys: ['visibility'],
    defaultPageSize: 50,
  });

  const pagesQuery = useQuery({
    queryKey: ['pages', 'list'],
    queryFn: ({ signal }) => listPages(signal),
    staleTime: 5_000,
  });

  const createMut = useMutation({
    mutationFn: () =>
      createPage({
        slug: newSlug.trim().replace(/^\/+/, ''),
        title: newTitle.trim() || null,
      }),
    onSuccess: ({ id }) => {
      void qc.invalidateQueries({ queryKey: ['pages', 'list'] });
      setShowCreate(false);
      setNewSlug('');
      setNewTitle('');
      void navigate({ to: '/pages/$id', params: { id } });
    },
    onError: (err) => {
      setCreateError(err instanceof ApiError ? err.message : 'Something went wrong.');
    },
  });

  const setActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updatePage(id, { isActive }),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ['pages', 'list'] });
      toast(vars.isActive ? 'Page is live' : 'Page hidden', { tone: 'success' });
    },
  });

  const removePage = useMutation({
    mutationFn: (id: string) => deletePage(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pages', 'list'] });
      toast('Page deleted', { tone: 'success' });
    },
  });

  const filtered = useMemo(() => {
    let all = pagesQuery.data ?? [];
    const visibility = table.filters.visibility;
    if (visibility === 'live') all = all.filter((p) => p.isActive);
    else if (visibility === 'hidden') all = all.filter((p) => !p.isActive);

    const needle = table.q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (p) =>
        p.slug.toLowerCase().includes(needle) || (p.title ?? '').toLowerCase().includes(needle),
    );
  }, [pagesQuery.data, table.q, table.filters.visibility]);

  const sorted = useMemo(() => {
    const rows = sortRows(filtered, table.sort, {
      slug: (p) => p.slug,
      title: (p) => p.title ?? p.slug,
      blockCount: (p) => p.blockCount,
      updatedAt: (p) => p.updatedAt,
      isActive: (p) => (p.isActive ? 1 : 0),
    });
    // The homepage always leads, whatever the sort — it is the page editors
    // reach for most and hunting for it in an alphabetical list is friction.
    return [...rows].sort((a, b) => (a.isHomepage === b.isHomepage ? 0 : a.isHomepage ? -1 : 1));
  }, [filtered, table.sort]);

  const pageRows = useMemo(
    () => paginate(sorted, table.page, table.pageSize),
    [sorted, table.page, table.pageSize],
  );

  const columns: Column<PageSummary>[] = [
    {
      key: 'title',
      header: 'Page',
      sortable: true,
      alwaysVisible: true,
      render: (page) => {
        const path = page.slug === '' ? '/' : `/${page.slug}`;
        return (
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/pages/$id"
                params={{ id: page.id }}
                className="font-medium text-admin-900 hover:underline"
              >
                {page.title ?? (page.isHomepage ? 'Home' : path)}
              </Link>
              {page.isHomepage && <StatusBadge status="homepage" label="Homepage" tone="info" />}
            </div>
            <span className="mt-0.5 block truncate font-mono text-xs text-admin-500">{path}</span>
          </div>
        );
      },
    },
    {
      key: 'isActive',
      header: 'Visibility',
      sortable: true,
      render: (page) => (
        <StatusBadge
          status={page.isActive ? 'published' : 'hidden'}
          label={page.isActive ? 'Live' : 'Hidden'}
          dot
        />
      ),
    },
    {
      key: 'blockCount',
      header: 'Blocks',
      sortable: true,
      align: 'end',
      render: (page) => <span className="text-admin-700">{page.blockCount}</span>,
    },
    {
      key: 'updatedAt',
      header: 'Updated',
      sortable: true,
      align: 'end',
      hideBelow: 'md',
      render: (page) => (
        <span className="text-xs text-admin-500">{formatDateTime(page.updatedAt)}</span>
      ),
    },
    actionsColumn<PageSummary>({
      label: (page) => page.title ?? (page.isHomepage ? 'Home' : `/${page.slug}`),
      primary: (page) => ({
        id: 'edit',
        label: 'Edit',
        icon: 'edit',
        onSelect: () => void navigate({ to: '/pages/$id', params: { id: page.id } }),
      }),
      actions: (page) => [
        {
          id: 'view',
          label: 'View live',
          icon: 'external',
          onSelect: () => window.open(`${marketingOrigin()}/${page.slug}`, '_blank', 'noreferrer'),
          disabled: !page.isActive,
          disabledReason: 'This page is hidden, so it has no public URL.',
        },
        {
          id: 'visibility',
          label: page.isActive ? 'Hide from the site' : 'Make live',
          icon: page.isActive ? 'eye' : 'checkCircle',
          onSelect: () => setActive.mutate({ id: page.id, isActive: !page.isActive }),
        },
        {
          id: 'delete',
          label: 'Delete page',
          icon: 'trash',
          destructive: true,
          onSelect: () => setPendingDelete(page),
          // The homepage has no replacement — deleting it would 404 the root.
          disabled: page.isHomepage,
          disabledReason: 'The homepage cannot be deleted.',
        },
      ],
    }),
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Pages"
        description="Marketing pages, composed from typed blocks. Edits are live — there is no publish step; hide a page by switching it off, and use snapshots to undo."
        actions={
          <Button onClick={() => setShowCreate(true)}>
            <Icon name="plus" className="h-4 w-4" />
            New page
          </Button>
        }
      />

      {showCreate && (
        <form
          className="mb-6 rounded-2xl border border-admin-200 bg-white p-5"
          onSubmit={(e) => {
            e.preventDefault();
            setCreateError(null);
            createMut.mutate();
          }}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm font-semibold text-admin-900">
              Slug
              <Input
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value)}
                placeholder="about, for-parents, legal/privacy"
                className="mt-1 font-mono text-sm"
                autoFocus
              />
              <span className="mt-1 block text-xs font-normal text-admin-500">
                Lowercase, kebab-case. Slashes nest. Empty slug = the home page.
              </span>
            </label>
            <label className="block text-sm font-semibold text-admin-900">
              Admin title <span className="font-normal text-admin-500">(optional)</span>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="About us"
                className="mt-1 text-sm"
              />
            </label>
          </div>
          {createError && (
            <p role="alert" className="mt-3 text-sm text-error-600">
              {createError}
            </p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" type="button" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={createMut.isPending}>
              Create page
            </Button>
          </div>
        </form>
      )}

      <DataTable
        columns={columns}
        rows={pageRows}
        rowKey={(p) => p.id}
        loading={pagesQuery.isLoading}
        error={pagesQuery.error}
        onRetry={() => void pagesQuery.refetch()}
        sort={table.sort}
        onSortChange={table.setSort}
        onRowClick={(p) => void navigate({ to: '/pages/$id', params: { id: p.id } })}
        caption="Marketing pages"
        empty={{
          icon: 'page',
          title: table.isFiltered ? 'No pages match these filters' : 'No pages yet',
          description: table.isFiltered
            ? 'Try clearing the filters to see everything.'
            : 'Create the first marketing page to get started.',
          action: !table.isFiltered ? (
            <Button onClick={() => setShowCreate(true)}>New page</Button>
          ) : undefined,
        }}
        toolbar={
          <FilterBar
            query={table.q}
            onQueryChange={table.setQuery}
            searchPlaceholder="Filter by slug or title…"
            filters={[
              {
                key: 'visibility',
                label: 'Visibility',
                value: table.filters.visibility ?? '',
                options: [
                  { value: 'live', label: 'Live' },
                  { value: 'hidden', label: 'Hidden' },
                ],
              },
            ]}
            onFilterChange={table.setFilter}
            onClear={table.clearFilters}
            isFiltered={table.isFiltered}
          />
        }
        footer={
          <Pagination
            page={table.page}
            pageSize={table.pageSize}
            total={sorted.length}
            onPageChange={table.setPage}
            onPageSizeChange={table.setPageSize}
          />
        }
      />

      <ConfirmDialog
        open={pendingDelete != null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete this page"
        description={
          <>
            <span className="font-medium">
              {pendingDelete?.title ?? `/${pendingDelete?.slug ?? ''}`}
            </span>{' '}
            and its {pendingDelete?.blockCount ?? 0} block
            {pendingDelete?.blockCount === 1 ? '' : 's'} will be removed. Marketing is SSR, so the
            page stops resolving immediately.
          </>
        }
        confirmLabel="Delete page"
        tone="danger"
        onConfirm={async () => {
          if (!pendingDelete) return;
          await removePage.mutateAsync(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}

/** Where a live page can be previewed. Mirrors lib/auth's origin resolution. */
function marketingOrigin(): string {
  const host = window.location.hostname;
  if (host === 'admin.fajrarabi.com') return 'https://fajrarabi.com';
  if (host === 'staging-admin.fajrarabi.com') return 'https://staging.fajrarabi.com';
  return 'http://localhost:5173';
}

function formatDateTime(iso: string): string {
  // Fixed en-GB so dates never reorder with the viewer's locale — staff compare
  // these across screens and an ambiguous 03/04 is a real hazard.
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
